import type { IAccount } from "../Accounting"
import type { IAddress } from "../Addresses"
import type { IContact } from "../Contacts"
import type { ITenant } from "../Admin/Tenants"
import type { AxiosError } from "axios"
import type { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { IUser } from "../Auth"
import { TTeam } from "../Teams"

export interface ITripSource {
	id: number
	name: string
	short_name: string
	accounting_accounts?: Array<IAccount>
	contacts?: Array<IContact>
	trips_count?: number
	connected_tenant_id?: number
	connected_tenant: ITenant
	address?: IAddress
	/**
	 * Is this a B2B partner
	 */
	is_agent: boolean
	tags?: Array<ITripSourceTag>
	teams?: Array<TTeam>
	conversion_count?: number
	created_at: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
}

export interface ITripSourceTag {
	id: number
	name: string
	trip_source_count?: number
	style?: {
		background_color?: string
		text_color?: string
	}
	created_by: IUser
	created_at: string
	updated_at?: string
	updated_by?: IUser
}

/**
 * Creating a trip source (or attaching a contact to one) fails with a 422 when
 * the contact's email/phone already belongs to other sources. Those sources are
 * returned in full so we can offer to select one instead of making the user
 * search for it again. Only sources visible to us are returned, so this can be
 * empty even though the request was rejected as a duplicate.
 *
 * `withServerErrors` only forwards `message` and `errors` to the form, so this
 * must be read from the raw error before it is rethrown.
 */
export function getDuplicateTripSourcesFromError(
	error: unknown
): Array<ITripSource> {
	const data = (error as AxiosError | undefined)?.response?.data as
		| { duplicate_trip_sources?: Array<ITripSource> }
		| undefined
	return data?.duplicate_trip_sources || []
}

export function tripSourcesXHR(xhr: XHRInstance) {
	return {
		async getTripSources(
			params?: unknown
		): Promise<IListResponse<ITripSource>> {
			return xhr.get("/trip-sources", { params }).then((resp) => resp.data)
		},
	}
}
